/**
 * Edit-time compositing helpers for the layered mockup (spec 019). Pure, DOM-free
 * functions used when baking a template's reusable assets:
 *  - `fillStackForProfile` — the per-category fill order (only the phone layer is
 *    category-specific; it appears solely for a CLEAR phone case).
 *  - `extractShell` — the green-removed product shell (clear = semi-transparent
 *    plastic + glare; opaque = body around the hole). Reuses the green-spill pass.
 *  - `printQuadFromGreen` — the 4-corner print/design quad from the green region
 *    (green-spill → transparent hole → placement detection).
 *
 * The heavy detection (segmentation) and the final composite live elsewhere (the
 * native segmenter + the deterministic engine `render::mockup`); these are the pure
 * pieces that are unit-testable without a canvas/model.
 */
import type { Polygon } from "@genz/engine-bind";
import { applyGreenSpillKey, type GreenSpillKeyOptions } from "./chroma.js";
import { detectTransparentRegion } from "./placement.js";

/** Product categories the mockup pipeline supports (spec 019 FR-006). */
export type ProductCategory =
  | "phone_case"
  | "color_case"
  | "dark_case"
  | "uv_case"
  | "mug"
  | "generic";

/** A template's product profile — the only per-category configuration. */
export interface ProductProfile {
  category: ProductCategory;
  /** Clear sub-toggle — VALID FOR `phone_case` ONLY; adds a phone layer behind the design. */
  clear?: boolean;
  /** Whether a texture/effect layer is used (UV cases always force it on). */
  texture?: boolean;
}

/** A fill-stack layer placed by the print quad (back → front). */
export type FillKind = "phone" | "design" | "texture";

/**
 * The ordered fill stack (back → front) for a profile. `design` is always present;
 * `phone` only for a CLEAR phone case (clear is ignored for any other category);
 * `texture` when requested, and always for UV cases.
 */
export function fillStackForProfile(profile: ProductProfile): FillKind[] {
  const stack: FillKind[] = [];
  if (profile.category === "phone_case" && profile.clear) stack.push("phone");
  stack.push("design");
  if (profile.texture || profile.category === "uv_case") stack.push("texture");
  return stack;
}

/**
 * The product shell: the generated image with the green region removed (alpha 0) and
 * the body kept (clear plastic + glare overlay, or opaque body around the hole). Thin
 * wrapper over the green-spill pass so the bake has one named entry point.
 */
export function extractShell(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  opts?: GreenSpillKeyOptions,
): Uint8ClampedArray {
  return applyGreenSpillKey(rgba, width, height, opts);
}

/**
 * The 4-corner print/design quad from the green region: remove the green (→ a
 * transparent hole), then detect that region's placement quad. Returns the 4-point
 * normalized polygon (TL,TR,BR,BL) or null when no green region is found.
 */
export function printQuadFromGreen(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  opts?: GreenSpillKeyOptions,
): Polygon | null {
  const keyed = applyGreenSpillKey(rgba, width, height, opts);
  const result = detectTransparentRegion(keyed, width, height);
  return result ? result.quad : null;
}

/**
 * A product-silhouette coverage buffer (`w*h`, 255 = product) from an alpha-keyed
 * image — `alpha > threshold` ⇒ product. The background tool produces the keyed image
 * (segmenter mask applied, or classical border-flood removed); this turns it into the
 * coverage the engine compositor consumes.
 */
export function productMaskFromAlpha(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  threshold = 8,
): Uint8Array {
  const n = width * height;
  const mask = new Uint8Array(n);
  for (let i = 0; i < n; i++) mask[i] = rgba[i * 4 + 3] > threshold ? 255 : 0;
  return mask;
}

/** The baked template assets the engine compositor consumes (per-template, reusable). */
export interface BakedAssets {
  /** Green-removed product shell (RGBA, `width × height`). */
  shell: Uint8ClampedArray;
  /** Product silhouette coverage (`width*height`, 255 = product). */
  productMask: Uint8Array;
  /** 4-corner print/design quad, or null if no green region was found. */
  printQuad: Polygon | null;
  width: number;
  height: number;
  profile: ProductProfile;
}

/**
 * Bake the reusable template assets from the generated image's pixels + a product mask
 * (from the background tool). Pure: the canvas read and the segmenter call are the
 * caller's job (they vary by platform); this assembles the deterministic result.
 */
export function bakeFromPixels(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  productMask: Uint8Array,
  profile: ProductProfile,
  opts?: GreenSpillKeyOptions,
): BakedAssets {
  return {
    shell: extractShell(rgba, width, height, opts),
    productMask,
    printQuad: printQuadFromGreen(rgba, width, height, opts),
    width,
    height,
    profile,
  };
}
